import { ApolloQueryResult } from '@apollo/client';

import { GET_TRANSLATE_IMPROVEMENTS_QUERY } from './useGetTranslateImprovements.graphql';
import {
  IGetTranslateImprovementsQueryGraphQLResponse,
  IGetTranslateImprovementsQueryVariables,
  IHandleFetchMoreTranslateImprovementsParams,
  IHandleGetTranslateImprovementsParams,
  IUseGetTranslateImprovements,
  IUseGetTranslateImprovementsOptions,
} from './useGetTranslateImprovements.interfaces';

import { useKanziLazyQuery } from '../../apollo';

const useGetTranslateImprovements = (options?: IUseGetTranslateImprovementsOptions): IUseGetTranslateImprovements => {
  const { fetchPolicy, onCompleted, onError, query, token } = options ?? ({} as IUseGetTranslateImprovementsOptions);

  const [executeGetTranslateImprovements, { fetchMore, loading: isLoading }] = useKanziLazyQuery<
    IGetTranslateImprovementsQueryGraphQLResponse,
    IGetTranslateImprovementsQueryVariables
  >({
    options: {
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
      onCompleted: (data) => {
        if (!onCompleted) {
          return;
        }

        const {
          translateImprovement: {
            getTranslateImprovements: { items, pageInfo, totalCount },
          },
        } = data;

        onCompleted({
          items,
          pageInfo,
          totalCount,
        });
      },
      onError,
    },
    query: query ?? GET_TRANSLATE_IMPROVEMENTS_QUERY,
    token,
  });

  const handleFetchMoreTranslateImprovements = ({
    after,
    endDate,
    first,
    language,
    projectId,
    startDate,
    status,
  }: IHandleFetchMoreTranslateImprovementsParams): Promise<ApolloQueryResult<IGetTranslateImprovementsQueryGraphQLResponse>> => {
    return fetchMore({
      updateQuery: (
        previousQueryResult,
        {
          fetchMoreResult: {
            translateImprovement: { getTranslateImprovements: newlyFetchedTranslateImprovementConnection },
          },
        },
      ) => {
        const { items: newlyFetchedItems } = newlyFetchedTranslateImprovementConnection;

        if (!newlyFetchedItems.length) {
          return previousQueryResult;
        }

        const {
          translateImprovement: { getTranslateImprovements: previouslyFetchedTranslateImprovementConnection },
        } = previousQueryResult;

        const { items: previouslyFetchedItems } = previouslyFetchedTranslateImprovementConnection;

        return {
          ...previousQueryResult,
          translateImprovement: {
            ...previouslyFetchedTranslateImprovementConnection,
            getTranslateImprovements: {
              ...newlyFetchedTranslateImprovementConnection,
              items: [...previouslyFetchedItems, ...newlyFetchedItems],
            },
          },
        };
      },
      variables: {
        after,
        endDate,
        first,
        language,
        projectId,
        startDate,
        status,
      },
    });
  };

  const handleGetTranslateImprovements = ({
    after,
    endDate,
    first,
    language,
    projectId,
    startDate,
    status,
  }: IHandleGetTranslateImprovementsParams) => {
    executeGetTranslateImprovements({
      variables: {
        after,
        endDate,
        first,
        language,
        projectId,
        startDate,
        status,
      },
    });
  };

  return {
    handleFetchMoreTranslateImprovements,
    handleGetTranslateImprovements,
    isLoading,
  };
};

export { useGetTranslateImprovements };
